import React, { useState, useRef, useEffect } from 'react';
import { signOut } from 'firebase/auth';
import { auth, db } from '../firebase';
import { useAuthState } from 'react-firebase-hooks/auth';
import { doc, getDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import HomeIcon from '@mui/icons-material/Home';
import SaveIcon from '@mui/icons-material/Save';
import BugReportIcon from '@mui/icons-material/BugReport';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';

const logoHeader = process.env.PUBLIC_URL + '/logo-header.png';

export default function TopBar({
  onHome, onSave, onSaveAndQuit, onQuit, onSettings,
  isInGame, saveStatus, slotName,
  hasNewPatch, onPatchNotes,
  onReportBug, isAdmin, onOpenAdmin,
  settingsBadge,
}) {
  const [user] = useAuthState(auth);
  const navigate = useNavigate();
  const [hoverLogout, setHoverLogout] = useState(false);
  const [hoverSettings, setHoverSettings] = useState(false);
  const [showSaveMenu, setShowSaveMenu] = useState(false);
  const [firestoreName, setFirestoreName] = useState('');
  const saveRef = useRef(null);

  // Load display name from Firestore (overrides Firebase Auth displayName)
  useEffect(() => {
    if (!user) return;
    getDoc(doc(db, 'users', user.uid)).then(snap => {
      if (snap.exists() && snap.data().displayName) {
        setFirestoreName(snap.data().displayName);
      } else {
        setFirestoreName(user.displayName || user.email?.split('@')[0] || '');
      }
    }).catch(() => {
      setFirestoreName(user.displayName || user.email?.split('@')[0] || '');
    });
  }, [user]);

  const handleLogout = async () => {
    await signOut(auth);
    navigate('/login');
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (saveRef.current && !saveRef.current.contains(e.target)) setShowSaveMenu(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  if (!user) return null;

  const btnBase = {
    fontFamily: "'Monda', sans-serif", fontSize: '10px', letterSpacing: '1.5px',
    background: 'transparent', border: '1px solid rgba(255,255,255,0.2)',
    color: 'rgba(255,255,255,0.4)', padding: '5px 14px', borderRadius: '3px',
    cursor: 'pointer', transition: 'background 0.2s, border-color 0.2s, color 0.2s', display: 'flex', alignItems: 'center', gap: '6px',
  };

  const iconBtn = {
    background: 'transparent', border: 'none', cursor: 'pointer', padding: '4px',
    display: 'flex', alignItems: 'center', color: 'rgba(255,255,255,0.35)', transition: 'color 0.2s',
  };

  return (
    <div style={{
      position: 'fixed', top: 0, right: 0, left: 0, zIndex: 50,
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: '10px 24px', gap: '12px',
      background: 'rgba(0,0,0,0.85)',
      backdropFilter: 'blur(8px)',
      borderBottom: '1px solid rgba(255,255,255,0.06)',
    }}>

      {/* LEFT — Home button */}
      <div style={{ pointerEvents: 'all' }}>
        <button onClick={onHome}
          style={{ ...iconBtn, color: 'rgba(255,255,255,0.5)', gap: '6px', display: 'flex', alignItems: 'center' }}
          onMouseEnter={e => e.currentTarget.style.color = '#fff'}
          onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.5)'}>
          <HomeIcon style={{ fontSize: 20 }} />
        </button>
      </div>

      {/* CENTER — Logo */}
      <div style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', pointerEvents: 'none' }}>
        <img src={logoHeader} alt="Logo" style={{ height: '42px', objectFit: 'contain', filter: 'drop-shadow(0 0 8px rgba(255,255,255,0.15))' }}
          onError={e => { e.target.style.display='none'; }} />
      </div>

      {/* RIGHT — Patch badge + Report bug + Admin + Save + Settings + Logout */}
      <div style={{ pointerEvents: 'all', display: 'flex', alignItems: 'center', gap: '12px' }}>
        <span style={{ fontFamily: "'Monda', sans-serif", fontSize: '12px', letterSpacing: '1px', color: 'rgba(255,255,255,0.5)' }}>
          {firestoreName || user.displayName || user.email}
          {slotName && <span style={{ color: 'rgba(255,255,255,0.25)', marginLeft: '6px' }}>· {slotName}</span>}
        </span>

        {/* ── Patch Notes badge ── */}
        {hasNewPatch && (
          <button
            onClick={onPatchNotes}
            title="What's new"
            style={{
              position: 'relative',
              background: 'transparent', border: 'none', cursor: 'pointer',
              padding: '4px 6px',
              display: 'flex', alignItems: 'center', gap: '5px',
              fontFamily: "'Monda', sans-serif", fontSize: '9px', letterSpacing: '1.5px',
              color: 'rgba(255,255,255,0.4)',
              transition: 'color 0.2s',
            }}
            onMouseEnter={e => e.currentTarget.style.color = '#fff'}
            onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.4)'}
          >
            {/* red dot */}
            <span style={{
              width: '7px', height: '7px', borderRadius: '50%',
              background: '#e01010',
              boxShadow: '0 0 6px rgba(224,16,16,0.8)',
              flexShrink: 0,
              animation: 'wm-patch-pulse 2s ease-in-out infinite',
            }} />
            WHAT'S NEW
            <style>{`
              @keyframes wm-patch-pulse {
                0%, 100% { opacity: 1; box-shadow: 0 0 6px rgba(224,16,16,0.8); }
                50%       { opacity: 0.6; box-shadow: 0 0 10px rgba(224,16,16,0.4); }
              }
            `}</style>
          </button>
        )}

        {/* ── Report a bug ── */}
        {onReportBug && (
          <button onClick={onReportBug} title="Report a bug"
            style={iconBtn}
            onMouseEnter={e => e.currentTarget.style.color = '#e01010'}
            onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.35)'}>
            <BugReportIcon style={{ fontSize: 18 }} />
          </button>
        )}

        {/* ── Admin panel (admin only) ── */}
        {isAdmin && onOpenAdmin && (
          <button onClick={onOpenAdmin} title="Admin panel"
            style={iconBtn}
            onMouseEnter={e => e.currentTarget.style.color = '#d4af37'}
            onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.35)'}>
            <AdminPanelSettingsIcon style={{ fontSize: 18 }} />
          </button>
        )}

        <button onClick={onSettings}
          style={{ ...iconBtn, position: 'relative' }}
          onMouseEnter={e => e.currentTarget.style.color = '#fff'}
          onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.35)'}>
          <SettingsIcon style={{ fontSize: 18 }} />
          {settingsBadge && (
            <span style={{
              position: 'absolute', top: '2px', right: '2px',
              width: '7px', height: '7px', borderRadius: '50%',
              background: '#e01010', boxShadow: '0 0 6px rgba(224,16,16,0.8)',
            }} />
          )}
        </button>

        {/* Save status indicator */}
        {isInGame && saveStatus && (
          <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.52rem', color: saveStatus === 'saved' ? '#2ecc71' : 'rgba(255,255,255,0.5)', letterSpacing: '0.5px', transition: 'color 0.3s' }}>
            {saveStatus === 'saving' ? '⏳ Saving...' : '✓ Saved!'}
          </div>
        )}
        {/* Save dropdown — only in game */}
        {isInGame && <div ref={saveRef} style={{ position: 'relative' }}>
          <button
            onClick={() => setShowSaveMenu(p => !p)}
            style={{ ...btnBase, borderColor: showSaveMenu ? 'rgba(255,255,255,0.6)' : 'rgba(255,255,255,0.2)', color: showSaveMenu ? '#fff' : 'rgba(255,255,255,0.5)' }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.6)'; e.currentTarget.style.color = '#fff'; }}
            onMouseLeave={e => { if (!showSaveMenu) { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)'; e.currentTarget.style.color = 'rgba(255,255,255,0.5)'; } }}>
            <SaveIcon style={{ fontSize: 12 }} />
            SAVE
            <span style={{ fontSize: '8px', opacity: 0.6 }}>▼</span>
          </button>

          {showSaveMenu && (
            <div style={{
              position: 'absolute', top: 'calc(100% + 6px)', right: 0,
              background: 'rgba(8,8,8,0.98)', border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '6px', overflow: 'hidden', minWidth: '160px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.8)',
              zIndex: 100,
            }}>
              {[
                { label: 'SAVE', icon: '💾', action: () => { onSave?.(); setShowSaveMenu(false); } },
                { label: 'SAVE & QUIT', icon: '💾', action: () => { onSaveAndQuit?.(); setShowSaveMenu(false); }, color: '#d4af37' },
                { label: 'QUIT WITHOUT SAVE', icon: '⚠', action: () => { onQuit?.(); setShowSaveMenu(false); }, color: '#e74c3c' },
              ].map(({ label, icon, action, color }) => (
                <button key={label} onClick={action}
                  style={{ width: '100%', background: 'transparent', border: 'none', borderBottom: '1px solid rgba(255,255,255,0.05)', color: color || 'rgba(255,255,255,0.7)', fontFamily: "'Monda',sans-serif", fontSize: '10px', letterSpacing: '1.5px', padding: '10px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', transition: 'background 0.12s', textAlign: 'left' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.06)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <span>{icon}</span> {label}
                </button>
              ))}
            </div>
          )}
        </div>}

        <button onClick={handleLogout}
          onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.6)'; e.currentTarget.style.color = '#fff'; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)'; e.currentTarget.style.color = 'rgba(255,255,255,0.4)'; }}
          style={btnBase}>
          <LogoutIcon style={{ fontSize: 12 }} />
          LOGOUT
        </button>
      </div>
    </div>
  );
}
